import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

const DEFAULT_STRIPE_LINK = 'https://buy.stripe.com/6oUdRb5EQacfcba5U33Nm04';
const DEFAULT_BUTTON_TEXT = "Finalisez votre inscription en effectuant votre premier dépôt.";
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const Hero = () => {
  const [stripeLink, setStripeLink] = useState(DEFAULT_STRIPE_LINK);
  const [buttonText, setButtonText] = useState(DEFAULT_BUTTON_TEXT);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/settings`)
      .then(({ data }) => {
        if (data.stripe_link) setStripeLink(data.stripe_link);
        if (data.button_text) setButtonText(data.button_text);
      })
      .catch(() => {
        // Garde les valeurs par défaut si l'API est indisponible
      });
  }, []);

  const handlePaymentClick = () => {
    // Ouvrir Stripe dans une nouvelle fenêtre popup centrée
    const width = 500;
    const height = 700;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    
    window.open(
      stripeLink,
      'StripeCheckout',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`
    );
  };

  return (
    <section className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-b from-white to-gray-50">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#01C38D]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#01C38D]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="text-left space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              SwissBorg vous souhaite
              <br />
              <span className="bg-gradient-to-r from-[#01C38D] to-[#00A878] bg-clip-text text-transparent">
                la bienvenue en Gestion Privée
              </span>
            </h1>

            <div className="space-y-4">
              <p className="text-gray-700 text-lg leading-relaxed">
                Félicitations ! Votre profil a été reconnu comme éligible à notre service de gestion privée — un espace exclusif réservé aux investisseurs bénéficiant d'un accompagnement personnalisé et d'opportunités à haute valeur ajoutée.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                En intégrant notre gestion privée, vous accédez à un environnement privilégié, pensé pour la performance, la sécurité et la discrétion.
              </p>
              <p className="text-[#01C38D] text-xl font-semibold">
                Vos avantages exclusifs :
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#01C38D] rounded-full"></div>
                <p className="text-gray-700 text-lg">Accompagnement sur mesure : un analyste financier senior vous est dédié pour définir et suivre votre stratégie d'investissement.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#01C38D] rounded-full"></div>
                <p className="text-gray-700 text-lg">Produits d'épargne personnalisés : des solutions adaptées à votre profil d'investisseur et à vos objectifs financiers.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#01C38D] rounded-full"></div>
                <p className="text-gray-700 text-lg">Accès prioritaire aux opportunités du marché : ICOs, placements privés et nouveaux projets à fort potentiel.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#01C38D] rounded-full"></div>
                <p className="text-gray-700 text-lg">Outils de suivi avancés : tableaux de bord en temps réel, alertes marché et reporting sécurisé.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#01C38D] rounded-full"></div>
                <p className="text-gray-700 text-lg">Avantages exclusifs membres : conditions préférentielles, accès anticipé aux nouveautés et support prioritaire.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handlePaymentClick}
                className="bg-[#01C38D] hover:bg-[#00A878] text-white px-8 py-6 text-lg group"
                data-testid="hero-payment-button"
              >
                {buttonText}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          {/* Right content - Phone mockup */}
          <div className="relative lg:block hidden">
            <div className="relative w-full max-w-md mx-auto">
              {/* Phone frame */}
              <div className="relative bg-gradient-to-br from-[#01C38D]/30 to-[#00A878]/30 rounded-[3rem] p-3 shadow-2xl border border-[#01C38D]/30">
                <div className="bg-white rounded-[2.5rem] p-6 overflow-hidden">
                  {/* Status bar */}
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-gray-800 text-xs">9:41</span>
                    <div className="flex gap-1">
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>

                  {/* App content */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-gray-800 font-semibold text-xl">Portfolio</h3>
                      <div className="flex gap-2">
                        <button className="px-4 py-2 bg-[#01C38D] text-white rounded-lg text-sm font-medium">Buy</button>
                        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">Sell</button>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-[#01C38D]/10 to-[#00A878]/10 rounded-2xl p-6 border border-[#01C38D]/30">
                      <div className="text-gray-500 text-sm mb-1">Total Balance</div>
                      <div className="text-gray-900 text-3xl font-bold mb-1">$124,582.46</div>
                      <div className="text-[#01C38D] text-sm">+$2,845.32 (2.35%)</div>
                    </div>

                    {/* Crypto list */}
                    <div className="space-y-3">
                      {['BTC', 'ETH', 'BORG'].map((crypto, idx) => (
                        <div key={crypto} className="flex items-center justify-between bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#01C38D]/20 rounded-full flex items-center justify-center">
                              <span className="text-[#01C38D] font-bold text-xs">{crypto}</span>
                            </div>
                            <div>
                              <div className="text-gray-800 font-medium">{crypto}</div>
                              <div className="text-gray-500 text-xs">0.{Math.floor(Math.random() * 9000) + 1000}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-gray-800 font-medium">${(Math.random() * 50000 + 1000).toFixed(2)}</div>
                            <div className={`text-xs ${idx % 2 === 0 ? 'text-[#01C38D]' : 'text-red-400'}`}>
                              {idx % 2 === 0 ? '+' : '-'}{(Math.random() * 5).toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative glow */}
              <div className="absolute inset-0 bg-[#01C38D]/20 blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
